// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState, useMemo } from 'react';
import { IconButton, Badge, Typography, Popover, Box, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import downloadIcon from 'Assets/download-icon.png';
import uploadIcon from 'Assets/upload-kml-icon.png';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ShowUploadSingleFileDialogAction, ShowOpsTrainingDialogAction } from 'Features/fileUpload/fileUploadActions';
import { SetOrderIdAction } from 'Features/order/orderActions';
import stylesDef from './styles';

interface ComponentProps {
  expand: boolean;
  checked:boolean;
  toggleExpand: () => void;
  clickDownload: (event: React.MouseEvent<HTMLElement>) => void;
  orderId: string;
  siteId: string;
  showFileDownloadState: string;
  cadFiles: {
    fileName: string;
    siteId: string;
    expressAI: boolean;
    visible: boolean;
  }[];
}

export const CadDrawingsButtons: React.FC<ComponentProps> = ({ checked, siteId, expand, toggleExpand, orderId, clickDownload, showFileDownloadState, cadFiles }) => {
  const styles = stylesDef();
  const dispatch = useAppDispatch();

  const allDownloadFileStatuses = useAppSelector((state) => state.order?.dxfFileStatus);
  const allDownloadGeoJsonFileStatuses = useAppSelector((state) => state.order?.geoJsonFileStatus);
  const anyChangeInFileStatuses = useAppSelector((state) => state.order.anyChangeInFileStatuses);
  const resources = useAppSelector((state) => state.auth.resources);
  const opsTrainer = 'opsTrainer' in resources;
  const isDemoProject = useAppSelector((state) => state.project?.project.isDemoProject);
  const [areDownloadsReady, setAreDownloadsReady] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Filter download states to only include files from the current project
  const downloadFileStatuses = useMemo(() => {
    const currentProjectSiteIds = cadFiles.map(file => file?.siteId);
    return allDownloadFileStatuses.filter((fileStatus: any) => 
      currentProjectSiteIds.includes(fileStatus.siteId)
    );
  }, [allDownloadFileStatuses, cadFiles]);

  const downloadGeoJsonFileStatuses = useMemo(() => {
    const currentProjectSiteIds = cadFiles.map(file => file?.siteId);
    return allDownloadGeoJsonFileStatuses.filter((fileStatus: any) => 
      currentProjectSiteIds.includes(fileStatus.siteId)
    );
  }, [allDownloadGeoJsonFileStatuses, cadFiles]);
  
  useEffect(() => {

    if (!downloadFileStatuses.length && !downloadGeoJsonFileStatuses.length) {
      setAreDownloadsReady(false);
      setIsDownloading(false);
    } else {
      // All available cad files
      const allCadFiles = cadFiles.map(file => file?.siteId);

      // Check if ANY file has ongoing downloads
      const hasOngoingDownloads = [
        ...downloadFileStatuses,
        ...downloadGeoJsonFileStatuses
      ].some(file => 
        Object.values(file.fileReadyStatus).some(status => status === 0) && allCadFiles.includes(file.siteId)
      );

      const areAllFilesReady = [
        ...downloadFileStatuses,
        ...downloadGeoJsonFileStatuses
      ].every(file => 
        Object.values(file.fileReadyStatus).every(status => status !== 0) && allCadFiles.includes(file.siteId)
      );

      // Check if ALL files are ready (no ongoing downloads)
      // const areAllFilesReady = !hasOngoingDownloads;
      
      setAreDownloadsReady(areAllFilesReady);
      setIsDownloading(hasOngoingDownloads);
    }
  }, [downloadFileStatuses, downloadGeoJsonFileStatuses, anyChangeInFileStatuses, cadFiles]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isDownloading || areDownloadsReady) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const GradientCircularProgress = () => {
    return (
      <React.Fragment>
        <svg width={0} height={0}>
          <defs>
            <linearGradient id="my_gradient" x1="0%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#E3E4EA" />
              <stop offset="100%" stopColor="#2E4DEB" />
            </linearGradient>
          </defs>
        </svg>
        <CircularProgress sx={styles.circularProgress} />
      </React.Fragment>
    );
  }

  const open = Boolean(anchorEl);

  const DownloadButton = (
    <>
      <Badge color="success" overlap="circular" variant="standard" invisible={!areDownloadsReady} badgeContent={<CheckIcon sx={styles.checkIcon} />} sx={styles.badge}>
        {isDownloading ? <GradientCircularProgress /> : null}
        <IconButton
          aria-label="Download"
          sx={styles.button}
          onClick={clickDownload}
          title="Download files"
        >
          <img 
            src={downloadIcon} 
            alt="Download" 
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
          />
        </IconButton>
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        sx={styles.popover}
        aria-haspopup="true"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Box
          sx={styles.popoverText}
        >
          {areDownloadsReady ? (
            <>
              <Typography sx={styles.popoverTextLine}>Your files are ready to</Typography>
              <Typography sx={styles.popoverTextLine}>be downloaded!</Typography>
            </>
          ) : (
            <>
              <Typography sx={styles.popoverTextLine}>A download is already in</Typography>
              <Typography sx={styles.popoverTextLine}>progress. Once ready, this icon</Typography>
              <Typography sx={styles.popoverTextLine}>will light up with a green check.</Typography>
            </>
          )}
        </Box>
      </Popover>
    </>
  );

  const uploadDxfClick = (e: React.ChangeEvent<{}>) => {
    dispatch(SetOrderIdAction(orderId));
    // eslint-disable-next-line no-unused-expressions
    opsTrainer ? dispatch(ShowOpsTrainingDialogAction()) : dispatch(ShowUploadSingleFileDialogAction());
    e.stopPropagation();
  };

  const uploadButton = (
    <IconButton
      aria-label="Upload"
      sx={styles.button}
      onClick={(e) => uploadDxfClick(e)}
      title="Upload .dxf"
    >
      <img src={uploadIcon} alt="Upload" />
    </IconButton>
  );

  const LayersExpandButton = (
    <IconButton sx={styles.button} onClick={toggleExpand}>
      {expand
        ? <KeyboardArrowDownIcon fontSize="small" />
        : <KeyboardArrowRightIcon fontSize="small" />}
    </IconButton>
  );

  return (
    <>
      {uploadButton}
      {(!isDemoProject && !opsTrainer) && DownloadButton}
      {checked && LayersExpandButton}
    </>
  );
};

export default CadDrawingsButtons;
